import { cloneDeep } from "lodash";

import { MedicalSummarySections } from "@/Patient/Components/MedicalSummaryConfiguration/MedicalSummaryEnum";
import { SummaryLayoutItem } from "@/Patient/Models/SummaryLayoutItem";

class MedicalSummaryConfigurationHelper {
    /**
     * Get an array of default summary configuration grid layout
     */
    public static getOriginalSummaryConfigurationGridLayout(): SummaryLayoutItem[] {
        return [
            this.getSummaryLayoutItem(0, 0, 2, 1, MedicalSummarySections.PreventiveActivitiesSection),
            this.getSummaryLayoutItem(0, 1, 2, 1, MedicalSummarySections.FollowUpsSection),
            this.getSummaryLayoutItem(0, 2, 1, 1, MedicalSummarySections.ActiveProblemsSection),
            this.getSummaryLayoutItem(1, 2, 1, 1, MedicalSummarySections.ActiveMedicationSection),
            this.getSummaryLayoutItem(0, 3, 1, 1, MedicalSummarySections.MedicalAntecedentsSection),
            this.getSummaryLayoutItem(1, 3, 1, 1, MedicalSummarySections.AllergiesAndIntoleranceSection),
            this.getSummaryLayoutItem(0, 4, 1, 1, MedicalSummarySections.ChirurgicalAntecedentsSection),
            this.getSummaryLayoutItem(1, 4, 1, 1, MedicalSummarySections.SocialHabitsSection),
            this.getSummaryLayoutItem(0, 5, 1, 1, MedicalSummarySections.PsychiatricAntecedentsSection),
            this.getSummaryLayoutItem(1, 5, 1, 1, MedicalSummarySections.AlertsAndSpecialNeedsSection),
            this.getSummaryLayoutItem(0, 6, 1, 1, MedicalSummarySections.ObstetricalAntecedentsSestion),
            this.getSummaryLayoutItem(1, 6, 1, 1, MedicalSummarySections.ImmunizationsSection),
            this.getSummaryLayoutItem(1, 7, 1, 1, MedicalSummarySections.FamilyHistorySection)
        ];
    }

    public static getDisabledSummaryConfigurationGridLayout(): SummaryLayoutItem[] {
        return [
            this.getSummaryLayoutItem(0, 8, 2, 1, MedicalSummarySections.VitalSignsSection)
        ];
    }

    /**
     * Get either the default summary configuration settings or a specific layout for the current patient Id from the user settings or the default one from this helper class
     */
    public static getSpecificOrDefaultSummaryConfigurationGridLayout(): SummaryLayoutItem[] {
        const layouts = cacheStore.UserSettings?.SummaryConfigurationSettings?.SummaryLayouts;
        const layout = layouts?.[patientStore.getCurrentPatientContactId()] ?? layouts?.["default"];

        return layout
            ? cloneDeep(layout)
            : MedicalSummaryConfigurationHelper.getOriginalSummaryConfigurationGridLayout();
    }

    public static getProblemsDescription(): boolean {
        const showDescriptions = cacheStore.UserSettings?.SummaryConfigurationSettings?.ShowProblemsDescription;

        return (
            showDescriptions?.[patientStore.getCurrentPatientContactId()]
                ?? showDescriptions?.["default"]
                ?? true
        );
    }

    /**
     * Get an object of summary layout item
     * @param row The row index of the layout item.
     * @param column The column index of the layout item.
     * @param width The width of the layout item.
     * @param height The height of the layout item.
     * @param section The section of the medical summary.
     * @returns The created summary layout item object.
     */
    public static getSummaryLayoutItem(row: number, column: number, width: number, height: number, section: MedicalSummarySections): SummaryLayoutItem {
        return {
            Row: row,
            Column: column,
            Width: width,
            Height: height,
            Section: section
        } as SummaryLayoutItem;
    }

    /**
     * Get medical summary section label by medical summary section enum
     * @param section The section of the medical summary.
     * @returns The label associated with the given medical summary section.
     */
    public static getLabelBySection(section: MedicalSummarySections): string {
        switch (section) {
            case MedicalSummarySections.PreventiveActivitiesSection:
                return "PreventiveActivitiesLabel";
            case MedicalSummarySections.FollowUpsSection:
                return "FollowUpsLabel";
            case MedicalSummarySections.ActiveProblemsSection:
                return "ProblemesActifsLabel";
            case MedicalSummarySections.ActiveMedicationSection:
                return "ActiveMedicationLabel";
            case MedicalSummarySections.MedicalAntecedentsSection:
                return "AntecedentsMedicauxLabel";
            case MedicalSummarySections.AllergiesAndIntoleranceSection:
                return "AllergiesAndIntoleranceLabel";
            case MedicalSummarySections.ChirurgicalAntecedentsSection:
                return "AntecedentsChirurgicauxLabel";
            case MedicalSummarySections.SocialHabitsSection:
                return "SocialHabitsLabel";
            case MedicalSummarySections.PsychiatricAntecedentsSection:
                return "AntecedentsPsychiatriquesLabel";
            case MedicalSummarySections.AlertsAndSpecialNeedsSection:
                return "AlertsAndSpecialNeedsLabel";
            case MedicalSummarySections.ObstetricalAntecedentsSestion:
                return "AntecedentsObstetriquesLabel";
            case MedicalSummarySections.ImmunizationsSection:
                return "ImmunizationsLabel";
            case MedicalSummarySections.FamilyHistorySection:
                return "FamilyHistoryLabel";
            case MedicalSummarySections.VitalSignsSection:
                return "VitalSignsLabel";
            default:
                return "PreventiveActivitiesLabel";
        }
    }
}

export { MedicalSummaryConfigurationHelper };
